import { useAppStateContext } from "@src/appV2/App/useAppState";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { SHIFT_DETAILS_TIMEKEEPING_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { useReportFocusModeViolation } from "../api/useReportFocusModeViolation";
import { FOCUS_MODE_BACKGROUND_GRACE_PERIOD_MS } from "../constants";

interface UseFocusModeTrackingParams {
  shiftId: string;
  focusModeSessionId: string;
  isActive: boolean;
  isOnBreak: boolean;
}

export function useFocusModeTracking({
  shiftId,
  focusModeSessionId,
  isActive,
  isOnBreak,
}: UseFocusModeTrackingParams) {
  const { state } = useAppStateContext();
  const history = useHistory();
  const { mutate: reportViolation } = useReportFocusModeViolation();

  const backgroundTimestampRef = useRef<number | undefined>(undefined);
  const isOnBreakRef = useRef(isOnBreak);
  isOnBreakRef.current = isOnBreak;

  const reportViolationIfNeeded = useCallback(
    (violationType: "app_background" | "route_change", durationMs: number) => {
      if (isOnBreakRef.current) {
        return;
      }

      reportViolation({
        shiftId,
        focusModeSessionId,
        violationType,
        occurredAt: new Date().toISOString(),
        durationMs,
      });

      logEvent(APP_V2_USER_EVENTS.FOCUS_MODE_VIOLATION_DETECTED, {
        shiftId,
        focusModeSessionId,
        violationType,
        durationMs,
      });
    },
    [shiftId, focusModeSessionId, reportViolation]
  );

  // Track app background/foreground transitions
  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (!state.isAppActive) {
      backgroundTimestampRef.current = Date.now();
    } else if (backgroundTimestampRef.current !== undefined) {
      const durationMs = Date.now() - backgroundTimestampRef.current;
      backgroundTimestampRef.current = undefined;

      if (durationMs > FOCUS_MODE_BACKGROUND_GRACE_PERIOD_MS) {
        reportViolationIfNeeded("app_background", durationMs);
      }

      logEvent(APP_V2_USER_EVENTS.FOCUS_MODE_RESUMED, {
        shiftId,
        focusModeSessionId,
        backgroundDurationMs: durationMs,
      });
    }
  }, [state.isAppActive, isActive, shiftId, focusModeSessionId, reportViolationIfNeeded]);

  // Track route changes away from timekeeping page
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const unlisten = history.listen((location) => {
      if (isOnBreakRef.current) {
        return;
      }

      const timekeepingPathPattern = SHIFT_DETAILS_TIMEKEEPING_FULL_PATH.replace(
        ":shiftId",
        shiftId
      );

      if (!location.pathname.startsWith(timekeepingPathPattern)) {
        reportViolationIfNeeded("route_change", 0);
      }
    });

    return unlisten;
  }, [isActive, history, shiftId, reportViolationIfNeeded]);
}
